import React from 'react';

import { Avatar, Button, Card, CardBody, CardHeader, Link, Separator, Text } from '@change/ds-core-ui';
import { ArrowUpIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { ActionsMenu } from './components/ActionsMenu';

type Props = {
	name: string;
	title: string;
	description?: string;
	photoUrl: string;
	link?: string;
	onEdit?: () => void;
	onRemove?: () => void;
	onReadMore?: () => void;
	readMoreLabel?: string;
	className?: string;
};

export const EndorsementCard: React.FC<Props> = ({
	name,
	title,
	description,
	photoUrl,
	link,
	onEdit,
	onRemove,
	onReadMore,
	readMoreLabel,
	className,
}) => {
	const nameContent = (
		<Text variant="body" fontWeight="bold" size="large">
			{name}
		</Text>
	);

	return (
		<Card className={cn('gap-0! p-5!', className || 'bg-fill-accent-primary-weaker border-none')}>
			<CardHeader className="flex items-start justify-between p-0!">
				<div className="flex items-center gap-4">
					<Avatar size="large" alt={name} src={photoUrl} />
					<div className="flex flex-col justify-between">
						{link ? (
							<Link href={link} variant="secondary">
								<div className="flex flex-row gap-1.5">
									{nameContent}
									<div className="rotate-45 [&_svg]:h-[12px] [&_svg]:w-[12px]">
										<ArrowUpIcon />
									</div>
								</div>
							</Link>
						) : (
							nameContent
						)}
						<Text variant="body" size="small" fontWeight="normal" className="text-typography-weak">
							{title}
						</Text>
					</div>
				</div>
				{(onEdit || onRemove) && <ActionsMenu onEdit={onEdit} onRemove={onRemove} />}
			</CardHeader>
			{description && (
				<>
					<Separator orientation="horizontal" className="border-stroke-accent-primary-weak my-5 opacity-34" />
					<CardBody>
						<Text as="p" className="leading-7" expressive>
							{description}
						</Text>
						{onReadMore && readMoreLabel && (
							<Button
								onClick={onReadMore}
								className="text-typography-strong mt-4"
								data-testid="endorsement-toggle-button"
								variant="tertiary"
								size="x-small"
							>
								{readMoreLabel}
							</Button>
						)}
					</CardBody>
				</>
			)}
		</Card>
	);
};
